import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { DomSanitizer } from "@angular/platform-browser";
import { OdPaginationComponent } from "@shared/components/od-pagination.component";
import { Invoice } from "../../models/invoice.models";
import { InvoiceResendEmailService } from "../../services/invoice-resend-email.service";
import { InvoiceDrawerComponent } from "../invoice-drawer/invoice-drawer.component";

@Component({
  selector: "app-invoices-table",
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    OdPaginationComponent,
  ],
  template: `
    <div class="invoices-table-container">
      <div class="table-header">
        <div class="header-row">
          <div class="header-cell document-col">
            <span class="header-text">Documento</span>
          </div>
          <div class="header-cell folio-col">
            <span class="header-text">Folio</span>
          </div>
          <div class="header-cell date-col">
            <span class="header-text">Fecha</span>
          </div>
          <div class="header-cell amount-col">
            <span class="header-text">Monto</span>
          </div>
          <div class="header-cell location-col">
            <span class="header-text">Local</span>
          </div>
          <div class="header-cell receipt-type-col">
            <span class="header-text">Tipo de comprobante</span>
          </div>
          <div class="header-cell actions-col">
            <span class="header-text">Acciones</span>
          </div>
        </div>
      </div>
      <div class="table-body">
        @for (
          invoice of paginatedInvoices();
          track invoice.folio;
          let odd = $odd
        ) {
          <div
            class="data-row"
            [ngClass]="{ 'odd-row': odd, 'even-row': !odd }"
          >
            <div class="data-cell document-col">
              <span
                class="document-indicator"
                [ngClass]="{
                  boleta: invoice.documentType === 'boleta',
                  'nota-credito': invoice.documentType === 'nota-credito',
                  factura: invoice.documentType === 'factura',
                }"
              ></span>
              <span class="document-label">{{
                invoice.documentTypeLabel
              }}</span>
            </div>
            <div class="data-cell folio-col">
              <span class="folio-text">{{ invoice.folio }}</span>
            </div>

            <div class="data-cell date-col">
              <span class="date-text">{{ invoice.date }}</span>
            </div>

            <div class="data-cell amount-col">
              <span class="amount-text">{{ invoice.amount }}</span>
            </div>

            <div class="data-cell location-col">
              <span class="location-text"
                >{{ invoice.location }}
                {{
                  invoice.receiptType === "fisico"
                    ? " - " + invoice.locationName
                    : ""
                }}</span
              >
            </div>

            <div class="data-cell receipt-type-col">
              <span
                class="receipt-badge"
                [ngClass]="{
                  fisico: invoice.receiptType === 'fisico',
                  virtual: invoice.receiptType === 'virtual',
                }"
              >
                @if (invoice.receiptType === "fisico") {
                  <mat-icon svgIcon="receiptLongIcon"></mat-icon>
                }
                @if (invoice.receiptType === "virtual") {
                  <mat-icon svgIcon="languageIcon"></mat-icon>
                }
                <span class="receipt-badge-text">
                  {{ invoice.receiptTypeLabel }}
                </span>
              </span>
            </div>

            <div class="data-cell actions-col">
              <button class="action-button" (click)="onReceiptClick(invoice)">
                <mat-icon svgIcon="receiptIcon"></mat-icon>
              </button>
              <button class="action-button" (click)="onSendClick(invoice)">
                <mat-icon svgIcon="sendIcon"></mat-icon>
              </button>
            </div>
          </div>
        }
      </div>

      <app-od-pagination
        [totalItems]="totalInvoices()"
        [itemsPerPage]="itemsPerPage"
        [page]="currentPage()"
        (pageChange)="onPageChange($event)"
      >
      </app-od-pagination>
    </div>
  `,
  styleUrl: "./invoices-table.component.scss",
})
export class InvoicesTableComponent implements OnInit {
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private dialog = inject(MatDialog);
  private invoiceResendEmailService = inject(InvoiceResendEmailService);

  // Input properties
  invoices = input<Invoice[]>([]);
  isLoading = input<boolean>(false);
  isError = input<boolean>(false);

  currentPage = signal(1);
  itemsPerPage = 8;

  // Computed property to get paginated invoices
  paginatedInvoices = computed(() => {
    const invoices = this.invoices();
    const startIndex = (this.currentPage() - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return invoices.slice(startIndex, endIndex);
  });

  // Total count for pagination
  totalInvoices = computed(() => this.invoices().length);

  ngOnInit() {
    this.matIconRegistry.addSvgIcon(
      "receiptIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-receipt.svg",
      ),
    );
    this.matIconRegistry.addSvgIcon(
      "sendIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-send.svg",
      ),
    );

    // Register additional icons for receipt types
    this.matIconRegistry.addSvgIcon(
      "receiptLongIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-receipt-long.svg",
      ),
    );
    this.matIconRegistry.addSvgIcon(
      "languageIcon",
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-language.svg",
      ),
    );
  }

  onReceiptClick(invoice: Invoice) {
    const dialogRef = this.dialog.open(InvoiceDrawerComponent, {
      height: "100%",
      width: "300px",
      position: { right: "0px" },
      data: {
        ...{
          ...invoice,
          orderId: invoice.orderId || invoice.folio || "",
        },
        close: () => dialogRef.close(),
      },
      disableClose: false,
      restoreFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }

  onSendClick(invoice: Invoice) {
    // Open the resend email modal
    this.invoiceResendEmailService.openResendEmailModal(invoice);
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
  }
}
